import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import CancelConfirmDialog from '../../components/cancel-confirm-dialog';
import { showToast } from '../../utils';
import {
  useCreateBundleCostMutation,
  useDeleteBundleCostMutation,
  useGetAllMaterialGroupOptionsQuery,
  useUpdateBundleCostMutation,
} from './price-management-api-slice';
import { BundleCost, PriceVersion } from './types';

export type BundleCostModalMode = 'add' | 'edit' | 'view';

/** Supports purlin bucket lots of 27,500 per UDOMI-199. */
const MAX_SHEETS_PER_BUNDLE = 99999;

export interface BundleCostModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode: BundleCostModalMode;
  version: PriceVersion;
  bundleCost?: BundleCost | null;
}

// Validation: if sticksPerBundle > 0, feetPerStick required; if sheetsPerBundle > 0, itemsPerSheet required
const createSchema = (mode: BundleCostModalMode) => {
  const base = z.object({
    materialGroup: z.string().min(1, 'Group is required'),
    size: z.string().min(1, 'Size is required').max(30, 'Size must be 30 characters or less'),
    bundleCost: z
      .number({ invalid_type_error: 'Bundle Cost is required' })
      .min(0.01, 'Must be positive value')
      .max(9999999.99, 'Bundle cost cannot exceed $9,999,999.99'),
    sticksPerBundle: z.number().int().min(0).max(9999).optional(),
    feetPerStick: z
      .number({ invalid_type_error: 'Feet/ Stick is required' })
      .min(0, 'Must be 0 or greater')
      .max(999.9, 'Number must be less than or equal to 999.9')
      .refine(
        value => Math.round(value * 10) / 10 === value,
        'Feet/ Stick supports at most one decimal place'
      )
      .optional(),
    sheetsPerBundle: z
      .number()
      .int()
      .min(0)
      .max(
        MAX_SHEETS_PER_BUNDLE,
        `Sheets per bundle cannot exceed ${MAX_SHEETS_PER_BUNDLE.toLocaleString()}`
      )
      .optional(),
    itemsPerSheet: z.number().int().min(0).max(9999).optional(),
    // Items cut from one stick (end plates): Cost per Item = Cost per Stick ÷ Items per Stick
    itemsPerStick: z
      .number()
      .int()
      .min(0)
      .max(1000, 'Items per stick cannot exceed 1,000')
      .optional(),
    notes: z.string().max(500).optional(),
  });

  if (mode === 'view') return base;

  return base.superRefine((data, ctx) => {
    const hasSticks = (data.sticksPerBundle ?? 0) > 0;
    const hasSheets = (data.sheetsPerBundle ?? 0) > 0;

    if (hasSticks && (!data.feetPerStick || data.feetPerStick <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Feet/ Stick required when Sticks/ Bundle provided',
        path: ['feetPerStick'],
      });
    }
    if (hasSheets && (!data.itemsPerSheet || data.itemsPerSheet <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Items per Sheet required for sheet materials',
        path: ['itemsPerSheet'],
      });
    }
    if (!hasSticks && !hasSheets) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one measurement set (Sticks or Sheets) is required',
        path: ['sticksPerBundle'],
      });
    }
  });
};

type FormData = z.infer<ReturnType<typeof createSchema>>;

const BundleCostModal: React.FC<BundleCostModalProps> = ({
  open,
  onClose,
  onSuccess,
  mode,
  version,
  bundleCost,
}) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bundleCostDisplay, setBundleCostDisplay] = useState('');
  const [feetPerStickDisplay, setFeetPerStickDisplay] = useState('');
  const feetPerStickEditingRef = useRef(false);

  const { data: allMaterialGroups = [] } = useGetAllMaterialGroupOptionsQuery(undefined, {
    skip: !open,
  });
  const [createBundleCost, { isLoading: isCreating }] = useCreateBundleCostMutation();
  const [updateBundleCost, { isLoading: isUpdating }] = useUpdateBundleCostMutation();
  const [deleteBundleCost, { isLoading: isDeleting }] = useDeleteBundleCostMutation();

  const groupOptions = useMemo(
    () => allMaterialGroups.map(mg => mg.groupName),
    [allMaterialGroups]
  );

  const schema = useMemo(() => createSchema(mode), [mode]);

  const defaultValues: FormData = useMemo(
    () =>
      bundleCost
        ? {
            materialGroup: bundleCost.materialGroup,
            size: bundleCost.size,
            bundleCost: bundleCost.bundleCost,
            sticksPerBundle: bundleCost.sticksPerBundle ?? 0,
            feetPerStick: bundleCost.feetPerStick ?? 0,
            sheetsPerBundle: bundleCost.sheetsPerBundle ?? 0,
            itemsPerSheet: bundleCost.itemsPerSheet ?? 0,
            itemsPerStick: bundleCost.itemsPerStick ?? 0,
            notes: bundleCost.notes ?? '',
          }
        : {
            materialGroup: '',
            size: '',
            bundleCost: 0,
            sticksPerBundle: 0,
            feetPerStick: 0,
            sheetsPerBundle: 0,
            itemsPerSheet: 0,
            itemsPerStick: 0,
            notes: '',
          },
    [bundleCost]
  );

  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onTouched',
  });

  const { control, watch, formState, handleSubmit, reset } = methods;
  const watchedValues = watch();

  // Calculated values with debounce
  const [calculated, setCalculated] = useState({
    costPerFoot: 0,
    costPerInch: 0,
    costPerStick: 0,
    costPerSheet: 0,
    costPerItem: 0,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recalculate = useCallback((values: FormData) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const bundleCostVal = values.bundleCost || 0;
      const sticks = values.sticksPerBundle ?? 0;
      const feet = values.feetPerStick ?? 0;
      const sheets = values.sheetsPerBundle ?? 0;
      const items = values.itemsPerSheet ?? 0;
      const itemsPerStick = values.itemsPerStick ?? 0;

      let costPerFoot = 0;
      let costPerInch = 0;
      let costPerStick = 0;
      let costPerSheet = 0;
      let costPerItem = 0;

      if (sticks > 0 && feet > 0) {
        const totalFeet = sticks * feet;
        costPerFoot = totalFeet > 0 ? bundleCostVal / totalFeet : 0;
        costPerInch = costPerFoot / 12;
      }
      if (sticks > 0) {
        costPerStick = bundleCostVal / sticks;
      }
      if (sheets > 0) {
        costPerSheet = bundleCostVal / sheets;
        if (items > 0) {
          costPerItem = bundleCostVal / (sheets * items);
        }
      }
      if (sticks > 0 && itemsPerStick > 0) {
        costPerItem = costPerStick / itemsPerStick;
      }

      setCalculated({
        costPerFoot,
        costPerInch,
        costPerStick,
        costPerSheet,
        costPerItem,
      });
      debounceRef.current = null;
    }, 250);
  }, []);

  useEffect(() => {
    if (open) {
      recalculate(watchedValues);
    }
  }, [open, watchedValues, recalculate]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setBundleCostDisplay(defaultValues.bundleCost ? defaultValues.bundleCost.toFixed(2) : '');
      setFeetPerStickDisplay(defaultValues.feetPerStick ? String(defaultValues.feetPerStick) : '');
      feetPerStickEditingRef.current = false;
    }
  }, [open, JSON.stringify(defaultValues), reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCancel = () => {
    if (formState.isDirty) {
      setCancelDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false);
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    if (!bundleCost) return;
    try {
      await deleteBundleCost({ id: bundleCost.id, versionId: bundleCost.versionId }).unwrap();
      showToast('Bundle Cost deleted', 'success');
      setDeleteDialogOpen(false);
      handleClose();
      onSuccess?.();
    } catch {
      // Error toast shown by API
    }
  };

  const onSubmit = handleSubmit(async (data: FormData) => {
    try {
      if (mode === 'add') {
        const sticks = data.sticksPerBundle ?? 0;
        const feet = data.feetPerStick ?? 0;
        const sheets = data.sheetsPerBundle ?? 0;
        const items = data.itemsPerSheet ?? 0;
        await createBundleCost({
          versionId: version.id,
          materialGroup: data.materialGroup,
          size: data.size.trim(),
          bundleCost: data.bundleCost,
          sticksPerBundle: sticks > 0 ? sticks : undefined,
          feetPerStick: feet > 0 ? feet : undefined,
          sheetsPerBundle: sheets > 0 ? sheets : undefined,
          itemsPerSheet: items > 0 ? items : undefined,
          itemsPerStick: (data.itemsPerStick ?? 0) > 0 ? data.itemsPerStick : undefined,
          notes: data.notes?.trim() || undefined,
        }).unwrap();
        showToast('Bundle Cost saved successfully', 'success');
      } else if (mode === 'edit' && bundleCost) {
        const sticks = data.sticksPerBundle ?? 0;
        const feet = data.feetPerStick ?? 0;
        const sheets = data.sheetsPerBundle ?? 0;
        const items = data.itemsPerSheet ?? 0;
        await updateBundleCost({
          id: bundleCost.id,
          versionId: bundleCost.versionId,
          bundleCost: data.bundleCost,
          sticksPerBundle: sticks > 0 ? sticks : undefined,
          feetPerStick: feet > 0 ? feet : undefined,
          sheetsPerBundle: sheets > 0 ? sheets : undefined,
          itemsPerSheet: items > 0 ? items : undefined,
          itemsPerStick: (data.itemsPerStick ?? 0) > 0 ? data.itemsPerStick : undefined,
          notes: data.notes?.trim() || undefined,
          rowVersion: bundleCost.rowVersion,
        }).unwrap();
        showToast('Bundle Cost saved successfully', 'success');
      }
      handleClose();
      onSuccess?.();
    } catch {
      // Error toast shown by API
    }
  });

  const formatCurrency = (val: number) => (val > 0 ? `$${val.toFixed(2)}` : '--');

  const isReadOnly = mode === 'view';
  // Group and Size are identity fields — not updateable once created
  const isIdentityReadOnly = mode === 'edit' || mode === 'view';
  const isSubmitting = isCreating || isUpdating || isDeleting;

  const modalTitle =
    mode === 'add'
      ? 'Add Bundle Cost'
      : mode === 'edit'
        ? `Edit Bundle Cost - ${bundleCost?.size ?? ''}`
        : `View Bundle Cost - ${bundleCost?.size ?? ''} (Published)`;

  const hasSticks = (watchedValues.sticksPerBundle ?? 0) > 0;
  const hasSheets = (watchedValues.sheetsPerBundle ?? 0) > 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { width: 600 } }}
        disableEscapeKeyDown={formState.isDirty}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">{modalTitle}</Typography>
            {watchedValues.materialGroup && (
              <Chip
                label={watchedValues.materialGroup}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <Chip
              label={`${version.status} v${version.versionNumber}.0`}
              size="small"
              color={version.status === 'Draft' ? 'warning' : 'success'}
            />
          </Box>
          <IconButton onClick={handleCancel} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Group */}
            <Controller
              name="materialGroup"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Group"
                    fullWidth
                    size="small"
                    disabled={isIdentityReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    select
                    SelectProps={{ native: true }}
                    sx={
                      isIdentityReadOnly
                        ? { '& .MuiInputBase-input': { color: 'text.secondary' } }
                        : undefined
                    }
                  >
                    <option value="" />
                    {groupOptions.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </TextField>
                  <FormHelperText>Select the material category for this bundle cost</FormHelperText>
                </Box>
              )}
            />

            {/* Size */}
            <Controller
              name="size"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Size"
                    fullWidth
                    size="small"
                    disabled={isIdentityReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 30 }}
                    sx={
                      isIdentityReadOnly
                        ? { '& .MuiInputBase-input': { color: 'text.secondary' } }
                        : undefined
                    }
                  />
                  <FormHelperText>Enter material size designation (e.g., 2x2x16ga)</FormHelperText>
                </Box>
              )}
            />

            {/* Previous Bundle Cost - Edit/View only */}
            {mode !== 'add' && bundleCost?.previousBundleCost != null && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Previous Bundle Cost:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      bundleCost.bundleCost < (bundleCost.previousBundleCost ?? 0)
                        ? 'success.main'
                        : bundleCost.bundleCost > (bundleCost.previousBundleCost ?? 0)
                          ? 'error.main'
                          : 'text.primary',
                  }}
                >
                  ${(bundleCost.previousBundleCost ?? 0).toFixed(2)}
                </Typography>
              </Box>
            )}

            {/* Bundle Cost */}
            <Controller
              name="bundleCost"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Bundle Cost"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography> }}
                    onChange={e => {
                      setBundleCostDisplay(e.target.value);
                      field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0);
                    }}
                    onBlur={() => {
                      field.onBlur();
                    }}
                    value={bundleCostDisplay}
                  />
                  <FormHelperText>Total cost per bundle from supplier</FormHelperText>
                </Box>
              )}
            />

            {/* Sticks/ Bundle */}
            <Controller
              name="sticksPerBundle"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Sticks/ Bundle"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    value={field.value ?? ''}
                  />
                  <FormHelperText>Number of sticks in one bundle</FormHelperText>
                </Box>
              )}
            />

            {/* Feet/ Stick */}
            <Controller
              name="feetPerStick"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Feet/ Stick"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ min: 0.1, max: 999.9, step: 0.1 }}
                    onChange={e => {
                      feetPerStickEditingRef.current = true;
                      setFeetPerStickDisplay(e.target.value);
                      field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0);
                    }}
                    onBlur={() => {
                      feetPerStickEditingRef.current = false;
                      field.onBlur();
                    }}
                    value={
                      feetPerStickEditingRef.current ? feetPerStickDisplay : (field.value ?? '')
                    }
                  />
                  <FormHelperText>Length of each stick in feet</FormHelperText>
                </Box>
              )}
            />

            {/* Items per Stick (end plates) */}
            <Controller
              name="itemsPerStick"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Items per Stick"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    value={field.value ?? ''}
                  />
                  <FormHelperText>
                    End plates cut from one stick (Cost per Item = Cost per Stick ÷ Items)
                  </FormHelperText>
                </Box>
              )}
            />

            {/* Sheets per Bundle */}
            <Controller
              name="sheetsPerBundle"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Sheets per Bundle"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    value={field.value ?? ''}
                  />
                  <FormHelperText>Number of sheets in one bundle</FormHelperText>
                </Box>
              )}
            />

            {/* Items per Sheet */}
            <Controller
              name="itemsPerSheet"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Items per Sheet"
                    fullWidth
                    size="small"
                    type="number"
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    onChange={e => field.onChange(parseInt(e.target.value, 10) || 0)}
                    value={field.value ?? ''}
                  />
                  <FormHelperText>Number of individual items per sheet</FormHelperText>
                </Box>
              )}
            />

            {/* Calculated Results */}
            <Box
              sx={{
                p: 2,
                backgroundColor: 'warning.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Calculated Results
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                {hasSticks && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Cost/ Ft.:
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(calculated.costPerFoot)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Cost/Inch:
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(calculated.costPerInch)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Cost/Stick:
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(calculated.costPerStick)}
                      </Typography>
                    </Box>
                  </>
                )}
                {hasSheets && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Cost per Sheet:
                      </Typography>
                      <Typography variant="body2">
                        {formatCurrency(calculated.costPerSheet)}
                      </Typography>
                    </Box>
                    {watchedValues.itemsPerSheet && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Cost per Item:
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(calculated.costPerItem)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {/* Notes */}
            <Controller
              name="notes"
              control={control}
              render={({ field, fieldState }) => (
                <Box>
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    size="small"
                    multiline
                    rows={4}
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    inputProps={{ maxLength: 500 }}
                  />
                  <FormHelperText>Additional notes or specifications (optional)</FormHelperText>
                </Box>
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box>
            {mode === 'edit' && (
              <Button
                color="error"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isSubmitting}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            {mode !== 'view' && (
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={!formState.isValid || isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            )}
            {mode === 'view' && (
              <Button variant="contained" onClick={handleClose}>
                Close
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <CancelConfirmDialog
        open={cancelDialogOpen}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setCancelDialogOpen(false)}
        dialogTitle="Discard unsaved changes?"
        dialogContent="Are you sure you want to discard your changes? All entered data will be lost."
        continuePrompt="Keep Editing"
        cancelPrompt="Discard"
      />

      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this bundle cost?</DialogTitle>
        <DialogContent>
          <Typography>This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BundleCostModal;
